import { useEffect, useRef, useState } from 'react';
import type { Game } from '@/game/Game';
import { useReducedMotion } from '@/ui/hooks/useCountUp';
import { useServices } from '@/ui/services/ServicesContext';
import { CoinIcon } from './Icons';
import styles from './CoinFly.module.css';

interface Flight {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

const MAX_FLIGHTS = 4;

/**
 * A coin hops from the Tiệm counter to the HUD coin chip on every sale (M2-07), so the money visibly lands
 * where it is counted. Decoration only (the "+N" above the counter already says it): skipped with reduced
 * motion and when the chip or the counter is not on screen.
 */
export function CoinFly({ game }: { game: Game }) {
  const { bus } = useServices();
  const reduced = useReducedMotion();
  const [flights, setFlights] = useState<Flight[]>([]);
  const seq = useRef(0);
  const reducedRef = useRef(reduced);
  useEffect(() => {
    reducedRef.current = reduced;
  });

  useEffect(
    () =>
      bus.onAny((e) => {
        if (e.type !== 'SALE_COMPLETED' || reducedRef.current) return;
        const from = game.counterClientPoint();
        const chip = document.querySelector('[data-testid="hud-coins"]')?.getBoundingClientRect();
        if (!from || !chip) return;
        const to = { x: chip.left + 16, y: chip.top + chip.height / 2 };
        const flight = { id: ++seq.current, x: from.x, y: from.y, dx: to.x - from.x, dy: to.y - from.y };
        setFlights((list) => [...list, flight].slice(-MAX_FLIGHTS));
      }),
    [bus, game],
  );

  return (
    <>
      {flights.map((f) => (
        <span
          key={f.id}
          className={styles.coin}
          aria-hidden="true"
          style={{
            left: f.x,
            top: f.y,
            ['--dx' as string]: `${String(f.dx)}px`,
            ['--dy' as string]: `${String(f.dy)}px`,
          }}
          onAnimationEnd={() => {
            setFlights((list) => list.filter((x) => x.id !== f.id));
          }}
        >
          <CoinIcon size={22} />
        </span>
      ))}
    </>
  );
}
